import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client.js";
import { formatWindow } from "@mosaic/shared";
import { STAGE_LABEL, TAX_LABEL, denseCompanyLabel, whenLabel } from "../lib/format.js";
import {
  nextSort,
  readStoredSort,
  sortSituations,
  writeStoredSort,
  type Sort,
  type SortKey,
} from "../lib/tableSort.js";

/**
 * A header that orders the table.
 *
 * `aria-sort` goes on the `th` and the arrow is `aria-hidden`, so a screen reader is told
 * the state once by the property the table role defines for it rather than twice, the
 * second time as a bare glyph.
 */
function SortHeader({
  label,
  column,
  sort,
  onSort,
  numeric = false,
}: {
  label: string;
  column: SortKey;
  sort: Sort | null;
  onSort: (key: SortKey) => void;
  numeric?: boolean;
}) {
  const active = sort?.key === column;
  const dir = active ? sort.dir : null;
  return (
    <th
      className={numeric ? "num sortable" : "sortable"}
      aria-sort={dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none"}
    >
      <button
        type="button"
        className={active ? "sortbtn on" : "sortbtn"}
        onClick={() => onSort(column)}
        title={`Sort by ${label}`}
      >
        {label}
        <span className="sortarrow" aria-hidden="true">
          {dir === "asc" ? "\u2191" : dir === "desc" ? "\u2193" : "\u2195"}
        </span>
      </button>
    </th>
  );
}

/**
 * Table mode: a dense multi-column table for cross-situation comparison rather than
 * reading, so everything is one row, monospaced, and nothing wraps.
 */
export function TablePage() {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ["situations", "table"],
    queryFn: () => api.situations({ limit: 200 }),
  });

  /**
   * The reader's own last ordering, restored, and null until they have ever sorted, in
   * which case the table opens in the order the server sent. Unlike a default, a
   * remembered choice is legible: the header it applies to is lit and its arrow points the
   * way the rows run.
   *
   * Stored rather than held in memory or put in the URL, because Back restores a URL and
   * the nav link does not.
   */
  const [sort, setSort] = useState<Sort | null>(readStoredSort);
  const rows = useMemo(() => sortSituations(query.data?.items ?? [], sort), [query.data, sort]);

  /**
   * Written here rather than in an effect on `sort`, so that only a press ever writes. An
   * effect would also fire on mount and write back the value it had just read.
   */
  const onSort = (key: SortKey) => {
    const next = nextSort(sort, key);
    setSort(next);
    writeStoredSort(next);
  };

  return (
    <div className="read">
      <div className="tablewrap">
        <table className="dense">
          <thead>
            <tr>
              <SortHeader label="SpinCo" column="spinco" sort={sort} onSort={onSort} />
              <SortHeader label="Parent" column="parent" sort={sort} onSort={onSort} />
              <th>Stage</th>
              <th>Record</th>
              <th>Distribution</th>
              <SortHeader label="Days" column="days" sort={sort} onSort={onSort} numeric />
              <th>Tax</th>
              <th>Latest</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} onClick={() => navigate(`/situations/${s.id}`)}>
                {/*
                  Both sides with the ticker bracketed and the corporate suffix trimmed. A
                  table row has no title above it, so the ticker alone is unreadable to a
                  reader who does not know it, and the name alone drops the token a reader
                  would type into a broker; the suffix distinguishes nothing and width is
                  scarce here.
                */}
                <td>{denseCompanyLabel(s.spinco)}</td>
                <td>{denseCompanyLabel(s.parent)}</td>
                <td>{STAGE_LABEL[s.stage]}</td>
                <td>{s.recordDate ?? "—"}</td>
                {/*
                  A stated window fills this cell when no date is set, marked so it
                  cannot be read as one. `Days` stays "—": a countdown to a quarter is
                  a precision the filing did not offer.
                */}
                <td>
                  {s.distributionDate ??
                    (s.expectedCompletion ? (
                      <span className="est" title="Expected window stated in a filing — not a distribution date.">
                        {formatWindow(s.expectedCompletion)}
                      </span>
                    ) : (
                      "—"
                    ))}
                </td>
                <td className="num">{whenLabel(s.daysToDistribution, s.stage)}</td>
                <td>{TAX_LABEL[s.taxTreatment] ?? s.taxTreatment}</td>
                {/*
                  The latest filing opens in the reader rather than the situation.
                  `stopPropagation` keeps the row's own click from also firing and
                  navigating to the situation instead.
                */}
                <td>
                  {s.latestFiling ? (
                    <Link
                      to={`/filings/${s.latestFiling.accession}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {s.latestFiling.formType} {s.latestFiling.filingDate}
                    </Link>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {query.data?.items.length === 0 && (
          <div className="empty">
            <p>No situations yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}
